import _ from 'lodash'

import Rectangle from '@/model/rectangle'
import { OptimiserSheetNodeTypes } from '@/store/types'


/**
 * Optimiser Node in the sheet
 * Represents a node in the optimiser sheet binary tree
 * 
 */
export default class OptimiserNode {
  constructor(parentNode, data) {
    this.id = data.id;
    this.type = data.type;
    this.parentNode = parentNode;
    this.x = data.x;
    this.y = data.y;
    this.width = data.width;
    this.height = data.height;
    this.panelId = data.panelId || null;
    this.angle = data.angle || 0;
    this.flippedHorz = !!data.flippedHorz;
    this.flippedVert = !!data.flippedVert;
    this.splitDir = data.splitDir;
    this.childNodes = [null, null];
  }


  /**
   * Clone a copy of the optimiser node & all children
   * Note: Does not clone the link to the parent if this is the topmost node
   * Only cloned children get linked to the cloned parent. The top node gets seperated from the tree
   * Clone the sheet (or root node) to retain complete structure
   */
  clone() {
    let clonedNode = new OptimiserNode(null, {
      id: this.id,
      type: this.type,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      panelId: this.panelId,
      angle: this.angle,
      flippedHorz: this.flippedHorz,
      flippedVert: this.flippedVert,
      splitDir: this.splitDir
    }, this.rootNode)

    if(this.childNodes[0]) {
      clonedNode.childNodes[0] = this.childNodes[0].clone()
      clonedNode.childNodes[0].parentNode = clonedNode
    }
    if(this.childNodes[1]) {
      clonedNode.childNodes[1] = this.childNodes[1].clone()
      clonedNode.childNodes[1].parentNode = clonedNode
    }

    return clonedNode
  }


  /**
   * Get the rectangle bounds of the node
   */
  getRectangle() {
    return new Rectangle(this.x, this.y, this.width, this.height)
  }


  /**
   * Check if a rectangle intersects with this node rectangle
   * Returns true if overlapping or intersection occurs
   */
  isRectangleIntersection(x, y, width, height) {

    // Check if rectangle completely inside
    if(this.x <= x && (x+width) <= (this.x + this.width) && this.y <= y && (y+height) <= (this.y+this.height))
      return true

    // Check intersection
    return !((x+width) <= this.x || x >= (this.x+this.width) || (y+height) <= this.y || y >= (this.y+this.height));
  }


  /**
   * Check if the node contains a point
   */
  nodeContainsPoint(x, y) {
    if(x < this.x || x > this.x+this.width)
      return false
    if(y < this.y || y > this.y+this.height)
      return false

    return true    
  }


  /**
   * Check if the node has children
   */
  hasChildren() {
    if(this.type != OptimiserSheetNodeTypes.node)
      return false

    return (this.childNodes && this.childNodes[0] && this.childNodes[1])
  }


  /**
   * Get the left coordinate of the node
   */
  getLeft() {
    return this.x;
  }


  /**
   * Get the top coordinate of the node
   */
  getTop() {
    return this.y;
  }


  /**
   * Get the right coordinate of the node
   */
  getRight() {
    return this.x + this.width;
  }


  /**
   * Get the bottom coordinate of the node
   */
  getBottom() {
    return this.y + this.height;
  }



  /**
   * Clear the node
   * Kills children & sets this node to waste
   */
  clear() {
    this.type = OptimiserSheetNodeTypes.waste
    this.angle = 0
    this.flippedHorz = false;
    this.flippedVert = false;
    this.childNodes[0] = null
    this.childNodes[1] = null
  }


  /**
   * Generate a string description for the node
   * Used for development
   */
  toString() {
    return JSON.stringify({
      id: this.id,
      type: this.type,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      panelId: this.panelId,
      splitDir: this.splitDir
    })
  }

}